/**
 * Блок «Должник» и падежные формы (ТЗ п. 5.2.1, 5.2.2).
 *
 * Падежные формы пересчитываются автоматически при изменении ФИО, но ручная
 * правка юриста имеет приоритет и хранится в профиле: ни одна библиотека
 * склонений не даёт 100 %, а исправлять одно и то же при каждой перегенерации
 * недопустимо (FR-36).
 */

import { useEffect, useRef, useState } from 'react';
import { GRAMMATICAL_CASES, type CaseProfile, type GrammaticalCase } from '@bd/domain';
import { api } from '../../../api.js';
import { AddressField, CheckboxField, DateField, Field, InnField, SnilsField, TextField } from '../../../components/fields/index.js';
import type { RegionOption } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';
import { AllDocumentsImport } from './AllDocumentsImport.js';
import { noteDictionaryFailure } from '../../../storage/dictionary-health.js';

const CASE_TITLES: Record<GrammaticalCase, string> = {
  nominative: 'Именительный (кто?)',
  genitive: 'Родительный (кого?)',
  dative: 'Дательный (кому?)',
  accusative: 'Винительный (кого?)',
  instrumental: 'Творительный (кем?)',
  prepositional: 'Предложный (о ком?)',
};

const DOCUMENT_KINDS: Array<[value: string, title: string]> = [
  ['passport_rf', 'Паспорт гражданина Российской Федерации'],
  ['passport_foreign', 'Паспорт иностранного гражданина'],
  ['residence_permit', 'Вид на жительство'],
  ['temporary_id', 'Временное удостоверение личности'],
  ['other', 'Иной документ'],
];

export function DebtorBlock({ profile, update, readOnly, errorFor, onRegionDetected }: BlockProps & { onRegionDetected?: (code: string) => void }) {
  const d = profile.debtor;
  const [declining, setDeclining] = useState(false);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const lastName = useRef('');

  // Субъекты берутся из справочника судов: другого перечня в приложении нет,
  // и так гарантируется, что по выбранному субъекту суд найдётся
  useEffect(() => {
    void api
      .dictionary('courts', { limit: 200 })
      .then((r) =>
        setRegions(
          r.entries
            .map((e) => ({ code: String(e.regionCode ?? ''), name: String(e.regionName ?? '') }))
            .filter((e) => e.code && e.name)
            .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
        ),
      )
      .catch(noteDictionaryFailure);
  }, []);

  /**
   * Правка блока «Должник».
   *
   * Принимает готовые значения либо функцию от АКТУАЛЬНОГО состояния.
   * Второе нужно там, где новое значение считается от предыдущего —
   * например, поля документа, удостоверяющего личность: они правятся
   * все вместе, одним вложенным объектом, и вычисление от устаревшего
   * состояния теряло бы соседнее поле.
   */
  const set = (
    patch: Partial<CaseProfile['debtor']> | ((current: CaseProfile['debtor']) => Partial<CaseProfile['debtor']>),
  ) =>
    update((p) => ({
      ...p,
      debtor: { ...p.debtor, ...(typeof patch === 'function' ? patch(p.debtor) : patch) },
    }));

  // Пересчёт падежей при изменении ФИО или пола. Ручные правки сохраняются:
  // их отмечает признак manuallyEdited, и сервер их не затирает.
  useEffect(() => {
    const key = `${d.last}|${d.first}|${d.middle}|${d.gender}`;
    if (key === lastName.current) return;
    if (!d.last && !d.first) return;
    lastName.current = key;

    const timer = setTimeout(async () => {
      setDeclining(true);
      try {
        const { declension } = await api.decline(
          { last: d.last, first: d.first, middle: d.middle, gender: d.gender },
          profile.declension,
        );
        update((p) => ({ ...p, declension }));
      } catch {
        // Сервер недоступен — падежи заполняются вручную, генерация
        // всё равно потребует их непустыми (раздел 6.8).
      } finally {
        setDeclining(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [d.last, d.first, d.middle, d.gender, profile.declension, update]);

  const setCase = (grammaticalCase: GrammaticalCase, value: string) =>
    update((p) => ({
      ...p,
      declension: {
        fio: { ...p.declension.fio, [grammaticalCase]: value },
        // Отметка ручной правки: пересчёт больше не трогает это значение
        manuallyEdited: { ...p.declension.manuallyEdited, [grammaticalCase]: true },
      },
    }));

  return (
    <section className="block">
      <h2>Должник</h2>

      {/* Вся папка клиента разом: сведения раскладываются по разделам после проверки */}
      <AllDocumentsImport profile={profile} update={update} readOnly={readOnly} />

      <div className="row row-3">
        <TextField label="Фамилия" value={d.last} required readOnly={readOnly} error={errorFor('debtor.last')} onChange={(v) => set({ last: v })} />
        <TextField label="Имя" value={d.first} required readOnly={readOnly} error={errorFor('debtor.first')} onChange={(v) => set({ first: v })} />
        <TextField label="Отчество" value={d.middle ?? ''} readOnly={readOnly} onChange={(v) => set({ middle: v })} />
      </div>

      <div className="row row-3">
        <Field label="Пол" required error={errorFor('debtor.gender')}>
          <select value={d.gender ?? ''} disabled={readOnly} onChange={(e) => set({ gender: (e.target.value || null) as 'male' | 'female' | null })}>
            <option value="">—</option>
            <option value="male">Мужской</option>
            <option value="female">Женский</option>
          </select>
        </Field>
        <DateField label="Дата рождения" value={d.birthDate} required readOnly={readOnly} error={errorFor('debtor.birthDate')} onChange={(v) => set({ birthDate: v })} />
        <TextField label="Место рождения" value={d.birthPlace} required readOnly={readOnly} error={errorFor('debtor.birthPlace')} onChange={(v) => set({ birthPlace: v })} />
      </div>

      <div className="row row-2">
        <InnField value={d.inn} kind="person" required readOnly={readOnly} onChange={(v) => set({ inn: v })} />
        <SnilsField value={d.snils} required readOnly={readOnly} onChange={(v) => set({ snils: v })} />
      </div>

      <TextField
        label="Прежние фамилия, имя, отчество"
        value={(d.previousNames ?? []).join('; ')}
        hint="Через точку с запятой. Требуется формой приказа МЭР № 530"
        readOnly={readOnly}
        onChange={(v) => set({ previousNames: v.split(';').map((s) => s.trim()).filter(Boolean) })}
      />

      <h3>Документ, удостоверяющий личность</h3>
      <div className="row row-3">
        <Field label="Вид документа" required>
          <select value={d.idDocument.kind} disabled={readOnly} onChange={(e) => set((current) => ({ idDocument: { ...current.idDocument, kind: e.target.value as never } }))}>
            {DOCUMENT_KINDS.map(([value, title]) => (
              <option key={value} value={value}>
                {title}
              </option>
            ))}
          </select>
        </Field>
        <TextField label="Серия" value={d.idDocument.series} required readOnly={readOnly} error={errorFor('debtor.idDocument')} onChange={(v) => set((current) => ({ idDocument: { ...current.idDocument, series: v } }))} />
        <TextField label="Номер" value={d.idDocument.number} required readOnly={readOnly} onChange={(v) => set((current) => ({ idDocument: { ...current.idDocument, number: v } }))} />
      </div>

      <div className="row row-3">
        <TextField label="Кем выдан" value={d.idDocument.issuedBy} required readOnly={readOnly} error={errorFor('debtor.idDocument.issuedBy')} onChange={(v) => set((current) => ({ idDocument: { ...current.idDocument, issuedBy: v } }))} />
        <DateField label="Дата выдачи" value={d.idDocument.issuedAt} required readOnly={readOnly} error={errorFor('debtor.idDocument.issuedAt')} onChange={(v) => set((current) => ({ idDocument: { ...current.idDocument, issuedAt: v } }))} />
        <TextField label="Код подразделения" value={d.idDocument.departmentCode ?? ''} readOnly={readOnly} onChange={(v) => set((current) => ({ idDocument: { ...current.idDocument, departmentCode: v } }))} />
      </div>

      <h3>Адрес</h3>
      <CheckboxField label="Есть регистрация по месту жительства" checked={d.hasRegistration} readOnly={readOnly} onChange={(v) => set({ hasRegistration: v })} />

      {d.hasRegistration ? (
        <AddressField
          label="Адрес регистрации"
          value={d.registrationAddress}
          required
          readOnly={readOnly}
          error={errorFor('debtor.registrationAddress')}
          onChange={(address) => set({ registrationAddress: address })}
          regions={regions}
          onRegionDetected={onRegionDetected}
        />
      ) : (
        <AddressField
          label="Адрес места пребывания"
          value={d.stayAddress ?? { full: '' }}
          required
          readOnly={readOnly}
          error={errorFor('debtor.stayAddress')}
          onChange={(address) => set({ stayAddress: address })}
          regions={regions}
          onRegionDetected={onRegionDetected}
        />
      )}

      <div className="row row-2">
        <TextField label="Телефон" value={d.phone ?? ''} readOnly={readOnly} onChange={(v) => set({ phone: v })} />
        <TextField label="Электронная почта" value={d.email ?? ''} readOnly={readOnly} onChange={(v) => set({ email: v })} />
      </div>

      <h3>
        Падежные формы ФИО{' '}
        {declining && <span className="hint">пересчитываются…</span>}
      </h3>
      <p className="hint">
        Формы рассчитываются автоматически. Исправленная вручную форма помечается и больше не пересчитывается —
        правку не придётся повторять при перегенерации.
      </p>
      <div className="row row-3">
        {GRAMMATICAL_CASES.map((grammaticalCase) => (
          <Field
            key={grammaticalCase}
            label={CASE_TITLES[grammaticalCase]}
            error={grammaticalCase === 'nominative' ? errorFor('declension.fio') : null}
            hint={profile.declension.manuallyEdited?.[grammaticalCase] ? 'Исправлено вручную' : undefined}
          >
            <input
              value={profile.declension.fio[grammaticalCase] ?? ''}
              readOnly={readOnly}
              onChange={(e) => setCase(grammaticalCase, e.target.value)}
            />
          </Field>
        ))}
      </div>
    </section>
  );
}
